import { useState } from 'react';
import { X, UserPlus, Mail, Copy, Check } from 'lucide-react';
import { inviteTenant } from '../../services/supabase/tenantInvitations';

export default function InviteTenantModal({ isOpen, onClose, property }) {
  const [tenantEmail, setTenantEmail] = useState('');
  const [permissions, setPermissions] = useState({
    viewInspections: true,
    uploadPhotos: true,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [invitationLink, setInvitationLink] = useState(null);
  const [copied, setCopied] = useState(false);

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      // Normalize and validate email
      const normalizedEmail = tenantEmail.trim().toLowerCase();
      if (!normalizedEmail || !normalizedEmail.includes('@')) {
        throw new Error('Podaj poprawny adres email');
      }

      console.log('[InviteTenantModal] Sending invitation to:', normalizedEmail);

      // Send invitation
      const result = await inviteTenant(property.id, normalizedEmail, permissions);

      // Generate invitation link
      const link = `${window.location.origin}/accept-invitation?token=${result.invitation_token}`;
      setInvitationLink(link);

      // Don't close modal - show success with link
    } catch (err) {
      console.error('Error inviting tenant:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (!invitationLink) return;

    try {
      await navigator.clipboard.writeText(invitationLink);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy link:', err);
    }
  };

  const handleClose = () => {
    setTenantEmail('');
    setPermissions({ viewInspections: true, uploadPhotos: true });
    setError(null);
    setInvitationLink(null);
    setCopied(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-md w-full p-6 relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center">
            <UserPlus className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-800">
              Zaproś najemcę
            </h2>
            <p className="text-sm text-slate-600">
              {property && `${property.street} ${property.building_number}${property.apartment_number ? `/${property.apartment_number}` : ''}, ${property.city}`}
            </p>
          </div>
        </div>

        {!invitationLink ? (
          // Form state
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email input */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Adres email najemcy
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email"
                  value={tenantEmail}
                  onChange={(e) => setTenantEmail(e.target.value)}
                  placeholder="najemca@example.com"
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                />
              </div>
            </div>

            {/* Permissions */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-3">
                Uprawnienia
              </label>
              <div className="space-y-2">
                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={permissions.viewInspections}
                    onChange={(e) => setPermissions(prev => ({
                      ...prev,
                      viewInspections: e.target.checked
                    }))}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      Podgląd inspekcji
                    </p>
                    <p className="text-xs text-slate-500">
                      Najemca może przeglądać wszystkie inspekcje tej nieruchomości
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg hover:bg-slate-50 transition-colors">
                  <input
                    type="checkbox"
                    checked={permissions.uploadPhotos}
                    onChange={(e) => setPermissions(prev => ({
                      ...prev,
                      uploadPhotos: e.target.checked
                    }))}
                    className="mt-1 w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <div>
                    <p className="text-sm font-medium text-slate-700">
                      Dodawanie inspekcji okresowych
                    </p>
                    <p className="text-xs text-slate-500">
                      Najemca może tworzyć inspekcje okresowe i dodawać zdjęcia
                    </p>
                  </div>
                </label>
              </div>
            </div>

            {/* Error message */}
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                {error}
              </div>
            )}

            {/* Info */}
            <div className="p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <p className="font-semibold mb-1">📧 Jak to działa?</p>
              <p>Wygenerujemy link zaproszenia ważny przez 7 dni. Wyślij go najemcy emailem lub innym komunikatorem.</p>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={handleClose}
                className="flex-1 py-2.5 px-4 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
              >
                Anuluj
              </button>
              <button
                type="submit"
                disabled={loading || !tenantEmail}
                className="flex-1 py-2.5 px-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Generowanie...' : 'Wygeneruj zaproszenie'}
              </button>
            </div>
          </form>
        ) : (
          // Success state with link
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-xl">
              <p className="text-green-900 font-semibold mb-1">
                ✅ Zaproszenie wygenerowane!
              </p>
              <p className="text-sm text-green-800">
                Wyślij poniższy link do <strong>{tenantEmail}</strong>
              </p>
            </div>

            {/* Link display */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Link zaproszenia
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={invitationLink}
                  readOnly
                  className="flex-1 px-3 py-2.5 bg-slate-50 border border-slate-300 rounded-xl text-sm font-mono text-slate-700"
                />
                <button
                  onClick={handleCopyLink}
                  className="px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors flex items-center gap-2"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      Skopiowano
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      Kopiuj
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Info */}
            <div className="p-3 bg-amber-50 rounded-lg text-sm text-amber-900">
              <p className="font-semibold mb-1">⏰ Ważność:</p>
              <p>Link będzie ważny przez 7 dni. Po tym czasie musisz wygenerować nowe zaproszenie.</p>
            </div>

            {/* Close button */}
            <button
              onClick={handleClose}
              className="w-full py-2.5 px-4 bg-slate-600 text-white rounded-xl font-semibold hover:bg-slate-700 transition-colors"
            >
              Zamknij
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
